import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, User } from 'lucide-react';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const [mobileCompaniesOpen, setMobileCompaniesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Capabilities', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'Safety', path: '/safety' },
    { name: 'About', path: '/about' },
    { name: 'Insights', path: '/insights' },
    { name: 'Software', path: '/software-solutions' },
    { name: 'Contact', path: '/contact' },
  ];

  const companyLinks = [
    { name: 'EnergiPro Solutions — Saudi Arabia', path: '/' },
    { name: 'KIMKO — United Arab Emirates', path: '/companies/kimko' },
    { name: 'Petronix Energy Services — Egypt', path: '/companies/petronix' },
  ];

  return (
    <>
      <nav 
        className={`fixed w-full z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-slate-900/98 backdrop-blur-lg shadow-[0_4px_20px_rgba(0,0,0,0.3)] py-2' 
            : 'bg-slate-900/60 backdrop-blur-md border-b border-white/5 py-3'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center pl-3 md:pl-4">
              <Link to="/" className="flex items-center">
                <img 
                  src="/assets/energipro-navbar-logo.png" 
                  alt="EnergiPro" 
                  className="h-[42px] w-auto object-contain animate-logo-reveal md:h-[50px] lg:h-[60px]" 
                />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    location.pathname === link.path 
                      ? 'text-orange-500' 
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
              {/* Companies Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setCompaniesOpen(true)}
                onMouseLeave={() => setCompaniesOpen(false)}
              >
                <button
                  className={`text-[13px] font-medium tracking-wide transition-colors duration-200 flex items-center gap-1 ${
                    location.pathname.startsWith('/companies')
                      ? 'text-orange-500'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  COMPANIES
                  <svg className={`w-3 h-3 transition-transform ${companiesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {companiesOpen && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-slate-900 border border-slate-700 shadow-xl z-50">
                    {companyLinks.map((c) => (
                      <Link
                        key={c.path}
                        to={c.path}
                        className={`block px-4 py-3 text-sm transition-colors ${
                          location.pathname === c.path
                            ? 'text-orange-500 bg-slate-800'
                            : 'text-slate-300 hover:text-white hover:bg-slate-800'
                        }`}
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    location.pathname === link.path 
                      ? 'text-orange-500' 
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
            </div>

            {/* Desktop Right Actions */}
            <div className="hidden lg:flex items-center gap-4 ml-8 pl-6 border-l border-white/10 shrink-0">
              {/* Client Portal — secondary */}
              <Link
                to="/portal"
                title="Existing clients: track project status, documents, and communications"
                className="text-slate-300 hover:text-white border border-slate-500/50 hover:border-slate-400 px-4 py-2 rounded-sm text-[13px] font-medium tracking-wide transition-all duration-200 flex items-center gap-1.5 group relative"
              >
                <User size={14} />
                CLIENT PORTAL
                <span className="hidden group-hover:block absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-slate-800 text-slate-300 text-xs rounded whitespace-nowrap border border-slate-700 shadow-lg z-50">
                  Existing clients: track projects & documents
                </span>
              </Link>
              {/* Request Quote — primary */}
              <Link
                to="/request-quote"
                className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-sm font-bold text-sm transition-all duration-300 flex items-center gap-1 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(234,88,12,0.3)] active:scale-[0.97]"
              >
                REQUEST QUOTE <ChevronRight size={16} />
              </Link>
            </div>

            {/* Mobile menu toggle */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-slate-300 hover:text-white focus:outline-none"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="lg:hidden bg-slate-900 border-t border-slate-800 absolute w-full shadow-xl">
            <div className="px-4 pt-3 pb-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2.5 rounded-md text-base font-medium ${
                    location.pathname === link.path
                      ? 'text-orange-500 bg-slate-800'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {/* Mobile Companies */}
              <div className="border-t border-slate-800 mt-2 pt-3">
                <button
                  onClick={() => setMobileCompaniesOpen(!mobileCompaniesOpen)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800"
                >
                  COMPANIES
                  <svg className={`w-4 h-4 transition-transform ${mobileCompaniesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {mobileCompaniesOpen && (
                  <div className="ml-4 space-y-1 mt-1">
                    {companyLinks.map((c) => (
                      <Link
                        key={c.path}
                        to={c.path}
                        onClick={() => setIsOpen(false)}
                        className={`block px-3 py-2 rounded-md text-sm ${
                          location.pathname === c.path
                            ? 'text-orange-500 bg-slate-800'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                        }`}
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* Mobile CTAs */}
              <div className="pt-4 space-y-2 border-t border-slate-800 mt-2">
                <Link
                  to="/request-quote"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center bg-orange-600 text-white px-3 py-3 rounded-sm font-bold text-sm"
                >
                  REQUEST QUOTE
                </Link>
                <Link
                  to="/portal"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center border border-slate-500 text-slate-300 px-3 py-2.5 rounded-sm font-medium text-sm"
                >
                  CLIENT PORTAL
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Industry Signal Strip — below header */}
      <div className={`fixed w-full z-40 transition-all duration-500 ${
        scrolled ? 'top-[56px] md:top-[64px] lg:top-[72px]' : 'top-[62px] md:top-[72px] lg:top-[82px]'
      }`}>
        <div className="bg-slate-800/90 backdrop-blur-sm border-b border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-[11px] sm:text-xs text-slate-400 py-1.5 tracking-wide font-medium">
              Serving KSA &amp; GCC&nbsp;&nbsp;|&nbsp;&nbsp;Well Testing&nbsp;&nbsp;|&nbsp;&nbsp;Pressure Control&nbsp;&nbsp;|&nbsp;&nbsp;Engineering Services
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
